import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { getStatsSummary, getCustomers, getTopClients } from "../services/api";
import { toast } from "sonner";
import { 
  Download, 
  Filter,
  FileText,
  FileMinus,
  Euro,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Megaphone,
  Users,
  Briefcase
} from "lucide-react";

// Period presets
const PERIOD_OPTIONS = [
  { value: "this_month", label: "Ce mois" },
  { value: "last_month", label: "Mois dernier" },
  { value: "3_months", label: "3 derniers mois" },
  { value: "6_months", label: "6 derniers mois" },
  { value: "12_months", label: "12 derniers mois" },
  { value: "this_year", label: "Année en cours" },
  { value: "last_year", label: "Année précédente" },
  { value: "all", label: "Depuis le début" },
  { value: "custom", label: "Personnalisé" }
];

const STATUS_OPTIONS = [
  { value: "all", label: "Tous les statuts" },
  { value: "validated", label: "Validée" },
  { value: "sent", label: "Envoyée" },
  { value: "paid", label: "Payée" },
  { value: "partial", label: "Partielle" },
  { value: "overdue", label: "En retard" },
  { value: "unpaid", label: "Non payées" }
];

const DOC_TYPE_OPTIONS = [
  { value: "all", label: "Tous types" },
  { value: "invoice", label: "Factures" },
  { value: "credit", label: "Avoirs" }
];

const STATUS_BADGES = {
  draft: { label: "Brouillon", className: "bg-slate-100 text-slate-600" },
  validated: { label: "Validée", className: "bg-blue-50 text-blue-700" },
  sent: { label: "Envoyée", className: "bg-blue-50 text-blue-700" },
  paid: { label: "Payée", className: "bg-green-50 text-green-700" },
  partial: { label: "Partielle", className: "bg-amber-50 text-amber-700" },
  overdue: { label: "En retard", className: "bg-red-50 text-red-700" },
  rectified: { label: "Rectifiée", className: "bg-purple-50 text-purple-700" }
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR");
};

const ReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showTTC, setShowTTC] = useState(true);
  
  // Filters
  const [period, setPeriod] = useState("12_months");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("all");
  const [docType, setDocType] = useState("all");
  const [customerId, setCustomerId] = useState("all");
  
  // Data
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);

  // Client stats
  const [clientStats, setClientStats] = useState(null);
  const [clientSort, setClientSort] = useState("ca");
  const [clientCategory, setClientCategory] = useState("all");

  useEffect(() => {
    const loadClientStats = async () => {
      try {
        const params = { limit: 100, sort_by: clientSort };
        if (period === "custom") {
          if (dateFrom) params.date_from = dateFrom;
          if (dateTo) params.date_to = dateTo;
        } else {
          params.period = period;
        }
        if (clientCategory !== "all") params.category = clientCategory;
        const res = await getTopClients(params);
        setClientStats(res.data);
      } catch (err) {
        console.error("Error loading client stats", err);
      }
    };
    loadClientStats();
  }, [period, dateFrom, dateTo, clientSort, clientCategory]);

  // Load customers for filter
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const res = await getCustomers();
        setCustomers(res.data || []);
      } catch (err) {
        console.error("Error loading customers", err);
      }
    };
    loadCustomers();
  }, []);

  // Load TTC preference
  useEffect(() => {
    const saved = localStorage.getItem("dashboard_showTTC");
    if (saved !== null) {
      setShowTTC(saved === "true");
    }
  }, []);

  // Save TTC preference
  useEffect(() => {
    localStorage.setItem("dashboard_showTTC", showTTC ? "true" : "false");
  }, [showTTC]);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      
      if (period === "custom") {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      } else {
        params.period = period;
      }
      
      if (status && status !== "all") params.status = status;
      if (docType && docType !== "all") params.doc_type = docType;
      if (customerId && customerId !== "all") params.customer_id = customerId;
      
      const res = await getStatsSummary(params);
      setData(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des données");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [period, dateFrom, dateTo, status, docType, customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Export to CSV
  const exportCSV = () => {
    if (!data?.documents) return;
    
    const headers = [
      "Type", "Numéro", "Date", "Échéance", "Client", "Statut", 
      "Total HT", "Total TTC", "Payé", "Reste dû"
    ];
    
    const rows = data.documents.map(d => [
      d.doc_type === "invoice" ? "Facture" : "Avoir",
      d.number || "Brouillon",
      d.issue_date,
      d.due_date || "",
      d.customer_name,
      STATUS_BADGES[d.status]?.label || d.status,
      d.total_ht,
      d.total_ttc,
      d.amount_paid,
      d.amount_due
    ]);
    
    const csvContent = [
      headers.join(";"),
      ...rows.map(r => r.join(";"))
    ].join("\n");
    
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rapport_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success("Export CSV téléchargé");
  };

  // Get value based on HT/TTC
  const getValue = (htValue, ttcValue) => showTTC ? ttcValue : htValue;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rapports</h1>
          <p className="text-sm text-slate-500 mt-1">
            Analyse détaillée de votre activité
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* HT/TTC Toggle */}
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200">
            <Label className={`text-sm ${!showTTC ? 'font-medium text-slate-900' : 'text-slate-400'}`}>
              HT
            </Label>
            <Switch
              checked={showTTC}
              onCheckedChange={setShowTTC}
              className="data-[state=checked]:bg-[#26788A]"
            />
            <Label className={`text-sm ${showTTC ? 'font-medium text-slate-900' : 'text-slate-400'}`}>
              TTC
            </Label>
          </div>
          
          <Button
            variant="outline"
            className="btn-outline"
            onClick={exportCSV}
            disabled={!data?.documents?.length}
          >
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Filter size={16} />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Period */}
            <div>
              <Label className="text-xs text-slate-500">Période</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="mt-1 bg-white border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {PERIOD_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom dates */}
            {period === "custom" && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Du</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="mt-1 input-field"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Au</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="mt-1 input-field"
                  />
                </div>
              </>
            )}
            
            {/* Status */}
            <div>
              <Label className="text-xs text-slate-500">Statut</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1 bg-white border-slate-200">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Doc type */}
            <div>
              <Label className="text-xs text-slate-500">Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1 bg-white border-slate-200">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {DOC_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Customer */}
            <div>
              <Label className="text-xs text-slate-500">Client</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="mt-1 bg-white border-slate-200">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  <SelectItem value="all">Tous les clients</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name || `${c.first_name} ${c.last_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-teal-50">
                  <Euro className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">CA Net {showTTC ? "TTC" : "HT"}</p>
                  <p className="text-xl font-bold font-mono text-slate-900" data-testid="report-ca-net">
                    {formatCurrency(getValue(data.net.total_ht, data.net.total_ttc))}
                  </p>
                  <p className="text-[10px] text-slate-400">avoirs déduits</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-50">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Encaissé</p>
                  <p className="text-xl font-bold font-mono text-green-700">
                    {formatCurrency(data.invoices.paid_ttc)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-50">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Reste à encaisser</p>
                  <p className="text-xl font-bold font-mono text-amber-700">
                    {formatCurrency(data.invoices.pending_ttc)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">TVA collectée</p>
                  <p className="text-xl font-bold font-mono text-blue-700">
                    {formatCurrency(data.net.total_vat)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-50">
                  <FileMinus className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total avoirs</p>
                  <p className="text-xl font-bold font-mono text-red-600" data-testid="report-total-avoirs">
                    {formatCurrency(getValue(data.credit_notes?.total_ht, data.credit_notes?.total_ttc))}
                  </p>
                  <p className="text-[10px] text-slate-400">{data.credit_notes?.count || 0} avoir{(data.credit_notes?.count || 0) > 1 ? "s" : ""} émis</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Communication vs Autres */}
      {data?.by_category && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="bg-white border-slate-200" data-testid="report-ca-communication">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-50">
                  <Megaphone className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Prestations Communication {showTTC ? "TTC" : "HT"}</p>
                  <p className="text-xl font-bold font-mono text-slate-900">
                    {formatCurrency(getValue(data.by_category.communication.total_ht, data.by_category.communication.total_ttc))}
                  </p>
                  <p className="text-[10px] text-slate-400">{data.by_category.communication.count} facture{data.by_category.communication.count > 1 ? "s" : ""}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200" data-testid="report-ca-autres">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-100">
                  <Briefcase className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Autres prestations {showTTC ? "TTC" : "HT"}</p>
                  <p className="text-xl font-bold font-mono text-slate-900">
                    {formatCurrency(getValue(data.by_category.other.total_ht, data.by_category.other.total_ttc))}
                  </p>
                  <p className="text-[10px] text-slate-400">{data.by_category.other.count} facture{data.by_category.other.count > 1 ? "s" : ""}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats par client */}
      <Card className="bg-white border-slate-200" data-testid="report-client-stats">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Users size={16} />
              Statistiques par client ({clientStats?.total_customers || 0})
            </CardTitle>
            <div className="flex gap-2">
              <Select value={clientCategory} onValueChange={setClientCategory}>
                <SelectTrigger className="w-44 h-8 text-xs bg-white border-slate-200" data-testid="client-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Toutes prestations</SelectItem>
                  <SelectItem value="communication">Communication</SelectItem>
                  <SelectItem value="other">Autres prestations</SelectItem>
                </SelectContent>
              </Select>
              <Select value={clientSort} onValueChange={setClientSort}>
                <SelectTrigger className="w-40 h-8 text-xs bg-white border-slate-200" data-testid="client-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="ca">Meilleur CA</SelectItem>
                  <SelectItem value="paid">Plus encaissé</SelectItem>
                  <SelectItem value="count">Nb factures</SelectItem>
                  <SelectItem value="avg">Panier moyen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {clientStats?.clients?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-100">
                    <th className="pb-3 pr-4">#</th>
                    <th className="pb-3 pr-4">Client</th>
                    <th className="pb-3 pr-4 text-right">CA {showTTC ? "TTC" : "HT"}</th>
                    <th className="pb-3 pr-4 text-right">Encaissé</th>
                    <th className="pb-3 pr-4 text-right">Factures</th>
                    {clientCategory === "all" && (
                      <>
                        <th className="pb-3 pr-4 text-right">Communication</th>
                        <th className="pb-3 text-right">Autres</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {clientStats.clients.map((c, idx) => (
                    <tr
                      key={c.customer_id}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/reports/client/${c.customer_id}`)}
                      data-testid={`client-stat-row-${c.customer_id}`}
                    >
                      <td className="py-3 pr-4 text-sm text-slate-400 font-mono">{idx + 1}</td>
                      <td className="py-3 pr-4 text-sm font-medium text-slate-900 max-w-[220px] truncate">{c.name}</td>
                      <td className="py-3 pr-4 text-right font-mono text-sm font-medium">
                        {formatCurrency(showTTC ? c.invoiced_ttc : c.invoiced_ht)}
                      </td>
                      <td className="py-3 pr-4 text-right font-mono text-sm text-green-600">
                        {formatCurrency(c.paid_ttc)}
                      </td>
                      <td className="py-3 pr-4 text-right text-sm text-slate-600">{c.invoice_count}</td>
                      {clientCategory === "all" && (
                        <>
                          <td className="py-3 pr-4 text-right font-mono text-sm text-purple-600">
                            {c.communication_ttc > 0 ? formatCurrency(c.communication_ttc) : "-"}
                          </td>
                          <td className="py-3 text-right font-mono text-sm text-slate-600">
                            {c.other_ttc > 0 ? formatCurrency(c.other_ttc) : "-"}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Users size={28} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucun client sur cette période</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents table */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <FileText size={16} />
              Documents ({data?.documents?.length || 0})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#26788A]"></div>
            </div>
          ) : data?.documents?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-100">
                    <th className="pb-3 pr-4">Type</th>
                    <th className="pb-3 pr-4">N°</th>
                    <th className="pb-3 pr-4">Date</th>
                    <th className="pb-3 pr-4">Client</th>
                    <th className="pb-3 pr-4">Statut</th>
                    <th className="pb-3 pr-4 text-right">{showTTC ? "TTC" : "HT"}</th>
                    <th className="pb-3 pr-4 text-right">Payé</th>
                    <th className="pb-3 text-right">Dû</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.documents.map((doc) => (
                    <tr 
                      key={doc.id} 
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/document/${doc.id}`)}
                    >
                      <td className="py-3 pr-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          doc.doc_type === "invoice" ? "bg-blue-50 text-blue-700" : "bg-red-50 text-red-700"
                        }`}>
                          {doc.doc_type === "invoice" ? "Facture" : "Avoir"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 font-mono text-sm">
                        {doc.number || "Brouillon"}
                      </td>
                      <td className="py-3 pr-4 text-sm text-slate-600">
                        {formatDate(doc.issue_date)}
                      </td>
                      <td className="py-3 pr-4 text-sm text-slate-900 max-w-[200px] truncate">
                        {doc.customer_name}
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          STATUS_BADGES[doc.status]?.className || "bg-slate-100 text-slate-600"
                        }`}>
                          {STATUS_BADGES[doc.status]?.label || doc.status}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right font-mono text-sm">
                        {formatCurrency(showTTC ? doc.total_ttc : doc.total_ht)}
                      </td>
                      <td className="py-3 pr-4 text-right font-mono text-sm text-green-600">
                        {doc.amount_paid > 0 ? formatCurrency(doc.amount_paid) : "-"}
                      </td>
                      <td className="py-3 text-right font-mono text-sm text-amber-600">
                        {doc.amount_due > 0 ? formatCurrency(doc.amount_due) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <FileText size={32} className="mx-auto mb-3 opacity-50" />
              <p>Aucun document trouvé avec ces filtres</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
